/**
 * @fileoverview Branch filter combobox for a banking page's header filters row.
 * @description Wraps the shared `SearchableSelect` molecule with the banking module's own branch
 * option list ("All Branches" plus every real branch), so every banking page gets the same
 * type-to-filter branch picker without re-deriving its option list or copy. Ported from the
 * InsightBox Banking design system's own `PageFilterBar.tsx`. An optional `scope` prop (the page's
 * own `resolveBankingAccessScope()` result) narrows or locks the option list for a Bank Regional
 * Manager or Bank Branch Manager — omitted, every other role gets today's full, unrestricted list.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { ALL_BRANCHES_ID, BRANCHES } from '@/features/banking/constants/banking-options.constants'
import SearchableSelect from '@/shared/components/molecules/SearchableSelect'

import type { BankingAccessScope } from '@/features/banking/lib/banking-access-scope'

interface BranchFilterSelectProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  scope?: BankingAccessScope
}

/**
 * @component
 * @param {BranchFilterSelectProps} props - The control's current value and change handler.
 * @param {string} props.value - The currently-selected branch id (`ALL_BRANCHES_ID`, a region
 * pseudo-id, or a real branch id).
 * @param {(value: string) => void} props.onChange - Called with the newly-selected branch id.
 * @param {boolean} [props.disabled] - Disables the control regardless of `scope`. Off by default.
 * @param {BankingAccessScope} [props.scope] - The signed-in user's resolved access scope
 * (`resolveBankingAccessScope()`); when it locks the user to a branch/region, this narrows the
 * option list and/or disables the control. Omitted (or unrestricted) keeps today's full list.
 */
function BranchFilterSelect({ value, onChange, disabled = false, scope }: BranchFilterSelectProps) {
  const { t } = useTranslation()
  const options = scope?.options ?? [
    { value: ALL_BRANCHES_ID, label: t('banking.filters.allBranches') },
    ...BRANCHES.map((branch) => ({ value: branch.id, label: branch.name })),
  ]

  return (
    <SearchableSelect
      label={t('banking.filters.branch')}
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled || (scope?.locked ?? false)}
      placeholder={t('banking.filters.allBranches')}
      searchPlaceholder={t('banking.filters.searchBranches')}
      noMatchesLabel={t('banking.filters.noBranchesFound')}
    />
  )
}

export default BranchFilterSelect
